import type { Namespace } from 'i18next';
import type { LangCode } from './locales-config';

import dayjs from 'dayjs';
import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { toast } from 'src/components/snackbar';
import { useSettingsContext } from 'src/components/settings';

import { fallbackLng, supportedLngs, getCurrentLang } from './locales-config';

// ----------------------------------------------------------------------

function syncDocumentLocale(lang: LangCode, direction: string) {
  document.documentElement.lang = lang;
  document.documentElement.dir = direction;
}

function isSupportedLang(lang: string): lang is LangCode {
  return supportedLngs.some((supportedLng) => supportedLng === lang);
}

// ----------------------------------------------------------------------

export function useTranslate(namespace?: Namespace) {
  const settings = useSettingsContext();

  const { t, i18n } = useTranslation(namespace);
  const { t: tMessages } = useTranslation('messages');

  const currentLang = getCurrentLang(i18n.resolvedLanguage);

  const updateDirection = useCallback(
    (lang: LangCode) => {
      settings.setState({ direction: i18n.dir(lang) });
    },
    [i18n, settings]
  );

  const updateDayjsLocale = useCallback((lang: LangCode) => {
    const updatedLang = getCurrentLang(lang);
    dayjs.locale(updatedLang.adapterLocale);
  }, []);

  const handleChangeLang = useCallback(
    async (lang: LangCode) => {
      try {
        const changeLangPromise = i18n.changeLanguage(lang);

        toast.promise(changeLangPromise, {
          loading: tMessages('languageSwitch.loading'),
          success: () => tMessages('languageSwitch.success'),
          error: () => tMessages('languageSwitch.error'),
        });

        await changeLangPromise;

        updateDirection(lang);
        updateDayjsLocale(lang);
      } catch (error) {
        console.error(error);
      }
    },
    [i18n, tMessages, updateDayjsLocale, updateDirection]
  );

  const handleResetLang = useCallback(() => {
    handleChangeLang(fallbackLng);
  }, [handleChangeLang]);

  return {
    t,
    i18n,
    currentLang,
    onChangeLang: handleChangeLang,
    onResetLang: handleResetLang,
  };
}

// ----------------------------------------------------------------------

export function useLocaleDirectionSync() {
  const { i18n, currentLang } = useTranslate();
  const { state, setState } = useSettingsContext();

  const handleSync = useCallback(async () => {
    const direction = i18n.dir(currentLang.value);

    syncDocumentLocale(currentLang.value, direction);

    if (state.direction !== direction) {
      setState({ direction });
    }

    if (i18n.resolvedLanguage !== currentLang.value) {
      await i18n.changeLanguage(currentLang.value);
    }
  }, [currentLang.value, i18n, setState, state.direction]);

  useEffect(() => {
    handleSync();
  }, [handleSync]);

  return null;
}

// ----------------------------------------------------------------------

export function useQueryLocaleInitializer(queryKey = 'locale') {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslate();

  useEffect(() => {
    const locale = searchParams.get(queryKey);

    if (!locale || !isSupportedLang(locale) || i18n.resolvedLanguage === locale) return;

    i18n.changeLanguage(locale);
  }, [i18n, queryKey, searchParams]);
}
